/**
 * Системные плейсхолдеры контекста сообщения для Telegram-шаблонов
 * (send-message / custom-webhook).
 *
 * Префикс `_` намеренно отделяет системные ключи от named groups визуального
 * конструктора: тот генерирует имена из VARIABLE_TYPES (amount, ccy, date2 …)
 * без подчёркиваний, поэтому коллизии по построению невозможны. Если admin
 * в advanced-режиме всё же создаст группу `(?<_chat_id>…)` — captures имеют
 * приоритет над системными значениями (см. renderTemplate в
 * apps/api/src/modules/telegram-listener/bot.ts).
 *
 * Файл — контракт front↔back: API подставляет значения при рендере шаблона,
 * web показывает список подсказок в редакторе правил.
 *
 * **Таймзона:** `{_date}`/`{_time}` — всегда UTC. Сервер не знает TZ конечного
 * читателя сообщения, а инвариант проекта — «время храним и считаем в UTC»
 * (CLAUDE.md §6); локальное отображение (`toLocaleString('ru-RU')`) — приём
 * фронта, для server-side подстановки в чужой чат он неприменим.
 */

export interface TelegramSystemPlaceholder {
  /** Имя плейсхолдера без фигурных скобок, например `_chat_title`. */
  key: string;
  /** Короткое имя для UI-подсказки. */
  label: string;
  /** Пояснение для tooltip/списка в редакторе шаблона. */
  description: string;
}

export const TELEGRAM_SYSTEM_PLACEHOLDERS: TelegramSystemPlaceholder[] = [
  {
    key: '_chat_title',
    label: 'Название чата',
    description: 'Название чата-источника; если название не сохранено — подставляется chat_id',
  },
  {
    key: '_chat_id',
    label: 'ID чата',
    description: 'Telegram chat_id чата-источника (для супергрупп/каналов — в -100-формате)',
  },
  {
    key: '_message_id',
    label: 'ID сообщения',
    description: 'Telegram message_id исходного сообщения',
  },
  {
    key: '_message_link',
    label: 'Ссылка на сообщение',
    description:
      'Ссылка t.me/c/… на исходное сообщение — только для супергрупп/каналов; для обычных групп и личных чатов подставляется пустая строка',
  },
  {
    key: '_message_text',
    label: 'Текст сообщения',
    description:
      'Полный текст исходного сообщения (может быть длинным — лимит Telegram 4096 символов проверяется после подстановки)',
  },
  {
    key: '_date',
    label: 'Дата сообщения',
    description: 'Дата исходного сообщения в формате YYYY-MM-DD (UTC)',
  },
  {
    key: '_time',
    label: 'Время сообщения',
    description: 'Время исходного сообщения в формате HH:MM (UTC)',
  },
];

/**
 * Ссылка на сообщение в супергруппе/канале: `https://t.me/c/<internal>/<msgId>`,
 * где internal = chat_id без префикса `-100`. Для обычных групп и личных чатов
 * публичной ссылки не существует → пустая строка (плейсхолдер схлопывается).
 */
export function buildTelegramMessageLink(
  chatId: string,
  messageId: number | string | undefined,
): string {
  if (messageId === undefined || messageId === '') return '';
  if (!chatId.startsWith('-100')) return '';
  const internal = chatId.slice('-100'.length);
  if (!/^\d+$/.test(internal)) return '';
  return `https://t.me/c/${internal}/${messageId}`;
}

export interface TelegramSystemContextInput {
  /** chat_id источника строкой (как в ActionContext.source.chatId). */
  chatId: string;
  /** Название чата из telegram.listening_chats (может отсутствовать). */
  chatTitle?: string | null;
  /** Telegram message_id исходного сообщения. */
  messageId?: number | string;
  /** Текст исходного сообщения. */
  messageText?: string;
  /** Дата сообщения: Date | ISO-строка | unix-время (секунды или миллисекунды). */
  messageDate?: Date | string | number;
}

/**
 * Собирает Record «плейсхолдер → значение» для renderTemplate.
 * Все ключи из TELEGRAM_SYSTEM_PLACEHOLDERS присутствуют всегда; отсутствующее
 * значение = пустая строка, чтобы шаблон не оставался с «дырками» вида
 * `{_message_link}` в отправленном сообщении.
 */
export function buildTelegramSystemContext(
  input: TelegramSystemContextInput,
): Record<string, string> {
  const d = parseMessageDate(input.messageDate);
  return {
    _chat_title: input.chatTitle || input.chatId,
    _chat_id: input.chatId,
    _message_id: input.messageId !== undefined ? String(input.messageId) : '',
    _message_link: buildTelegramMessageLink(input.chatId, input.messageId),
    _message_text: input.messageText ?? '',
    // UTC — см. докстринг модуля про выбор таймзоны.
    _date: d ? d.toISOString().slice(0, 10) : '',
    _time: d ? d.toISOString().slice(11, 16) : '',
  };
}

/** Нормализация даты: Telegram отдаёт unix-секунды, БД — timestamptz (Date/ISO). */
function parseMessageDate(v: Date | string | number | undefined): Date | null {
  if (v === undefined || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === 'number') {
    // Эвристика: < 1e12 — unix-секунды (Telegram), иначе — миллисекунды.
    const d = new Date(v < 1e12 ? v * 1000 : v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
